import { Box, Group, Text } from "@mantine/core"
import type { ReactNode } from "react"
import { HelpIcon } from "@dehub/theme"
import { CheckCircleIcon } from "@phosphor-icons/react"
import classes from "./wizard.module.css"

/**
 * A numbered wizard card (canvas Ord_RouteWizard): number + title on the left, `aside` right.
 * helpKey adds the section «?» after the title; helpId is the spotlight / checklist target (@dehub/help).
 */
export function WizardSection({
  n,
  title,
  aside,
  helpKey,
  helpId,
  required,
  children,
}: Readonly<{ n?: number; title: ReactNode; aside?: ReactNode; helpKey?: string; helpId?: string; required?: boolean; children: ReactNode }>) {
  return (
    <Box component="section" className={classes.card} data-help-id={helpId}>
      <Box className={classes.head}>
        <Group gap="sm" wrap="nowrap" miw={0}>
          {n !== undefined && (
            <Box component="span" className={classes.num}>
              {n}
            </Box>
          )}
          <Text className={classes.title}>
            {title}
            {/* Same red asterisk as a required input label: the card itself must be filled (e.g. ≥1 order). */}
            {required && <Text span c="var(--mantine-color-error)" ml={4} aria-hidden>*</Text>}
            {helpKey && <HelpIcon helpKey={helpKey} variant="section" />}
          </Text>
        </Group>
        {aside}
      </Box>
      <Box className={classes.body}>{children}</Box>
    </Box>
  )
}

/** «✓ Бөглөгдсөн» and the step line of the action bar: filled = green check, not yet = hollow grey circle. */
export function StepMark({ done, children }: Readonly<{ done: boolean; children: ReactNode }>) {
  return (
    <Text span className={classes.status} data-done={done || undefined}>
      {done ? (
        <CheckCircleIcon size={18} weight="fill" className={classes.check} aria-hidden />
      ) : (
        <Box component="span" className={classes.ring} aria-hidden />
      )}
      {children}
    </Text>
  )
}
